/**
 * The recorder's timer (UC-8, light): how long has been recorded, pauses left out — the recorder
 * drops the audio of a pause, so the timer must not count it either. Pure: every step takes the
 * clock's reading (`performance.now()` in the recorder), so a pause is banked at the instant it
 * starts and a tick that comes late changes nothing.
 */
export interface Stopwatch {
  /** Milliseconds recorded before the current run. */
  bankedMs: number
  /** The clock's reading when the current run started; null while paused or not started. */
  runningSince: number | null
}

/** A stopwatch started now, from zero. */
export const startStopwatch = (now: number): Stopwatch => ({ bankedMs: 0, runningSince: now })

/** The time recorded so far. */
export function stopwatchElapsedMs(watch: Stopwatch, now: number): number {
  const running = watch.runningSince === null ? 0 : Math.max(0, now - watch.runningSince)
  return watch.bankedMs + running
}

/** Pause: what ran so far is banked. Pausing a paused stopwatch changes nothing. */
export function pauseStopwatch(watch: Stopwatch, now: number): Stopwatch {
  if (watch.runningSince === null) return watch
  return { bankedMs: stopwatchElapsedMs(watch, now), runningSince: null }
}

/** Resume: a new run starts now. Resuming a running stopwatch changes nothing. */
export function resumeStopwatch(watch: Stopwatch, now: number): Stopwatch {
  return watch.runningSince === null ? { ...watch, runningSince: now } : watch
}
